import { Directive, ElementRef, HostBinding, Input } from '@angular/core';
import { Nullable } from '@mosaik/core';
import { AriaRole, ElementExtensions } from '@mosaik/presentation';
import { FrameworkElement, IFrameworkElementProps } from '../../Abstracts/FrameworkElement';

/**
 * A property interface of all properties that contained in the {@link ElementComponent}.
 *
 * Extends the {@link IFrameworkElementProps} interface.
 * @public
 */
export interface IElementComponentProps<TElement extends HTMLElement = HTMLElement> extends IFrameworkElementProps<TElement> {
  role: Nullable<AriaRole>;
  foreground: Nullable<string>;
  background: Nullable<string>;
  defaultClass: string;
}

/**
 * todo doc
 *
 * Extends the {@link FrameworkElement} class.
 * Implements the {@link IElementComponentProps} interface.
 * @public
 */
@Directive()
export abstract class ElementComponent<TElement extends HTMLElement = HTMLElement>
  extends FrameworkElement<TElement>
  implements IElementComponentProps<TElement>
{
  private _role: Nullable<AriaRole>;
  private _foreground: Nullable<string>;
  private _background: Nullable<string>;
  private _defaultClass: string;

  public constructor(element: ElementRef<TElement>) {
    super(element.nativeElement);

    this._role = null;
    this._foreground = null;
    this._background = null;
    this.defaultClass = this._defaultClass = '';
  }

  /**
   * Gets or sets the HTML role attribute.
   * Sets the `role` dom attribute as value, otherwise nothing.
   * @protected
   * @type Nullable<AriaRole>
   * @default null
   */
  @Input()
  @HostBinding('attr.role')
  public get role(): Nullable<AriaRole> {
    return this._role;
  }
  public set role(value: Nullable<AriaRole>) {
    if (this._role !== value) {
      this._role = value;
    }
  }

  /**
   * Gets or sets the foreground color of the {@link ElementComponent}.
   * - Hex Code           - #RRGGBB
   * - Short Hex Code     - #RGB
   * - RGB %              - rgb(rrr%,ggg%,bbb%)
   * - RGB Absolute       - rgb(rrr,ggg,bbb)
   * - keyword            - aqua, black, etc.
   * @public
   * @type Nullable<string>
   * @default null
   */
  @Input()
  public get foreground(): Nullable<string> {
    return this._foreground;
  }
  public set foreground(value: Nullable<string>) {
    this._foreground = value;
  }

  /**
   * Gets or sets the background color of the {@link ElementComponent}.
   * - Hex Code           - #RRGGBB
   * - Short Hex Code     - #RGB
   * - RGB %              - rgb(rrr%,ggg%,bbb%)
   * - RGB Absolute       - rgb(rrr,ggg,bbb)
   * - keyword            - aqua, black, etc.
   * @public
   * @type Nullable<string>
   * @default null
   */
  @Input()
  public get background(): Nullable<string> {
    return this._background;
  }
  public set background(value: Nullable<string>) {
    this._background = value;
  }

  /**
   * Gets or sets the default class of the {@link ElementComponent}.
   * The `defaultClass` consists of the `ThemeManager` theme and the component name itself.
   * The `ThemeManager` is optional, in this case the `defaultClass` name is empty
   * @public
   * @type string
   * @default ''
   */
  @Input()
  public get defaultClass(): string {
    return this._defaultClass;
  }
  public set defaultClass(value: string) {
    this._defaultClass = value;
    ElementExtensions.toggleClass(this.nativeElement, this._defaultClass, '', this._defaultClass ? true : false);
  }

  /**
   *  @protected
   */
  public selector(): string {
    return this.nativeElement.tagName.toLowerCase() || '';
  }
}
